import React, { forwardRef, Suspense, useRef } from "react"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faShop } from "@fortawesome/pro-light-svg-icons"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { SUPPORT_ENTITIES_ROUTE, SUPPORT_ROUTE } from "../../../../../../constants/routes"
import { Button } from "../../../../../office/admin/components/Button"
import { LoadingSpinner } from "../../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../../core/api/components/QueryErrorBoundary"
import { FormikProps } from "formik"
import { useApiMutation } from "../../../../../../core/api/useApiMutation"
import { enqueueSnackbar } from "notistack"
import { fetchEntitiesById, updateCompanyApi } from "../createCompanyApi"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import { useParams } from "react-router-dom"
import { useNavigate } from "react-router"
import { CompanyFormValues, SupportCompanyWithHistory } from "../companyTypes"
import { CompanyCreateUpdateForm } from "../components/CompanyCreateUpdateForm"
import { AgreementOptions, BusinessType, EntityHistory, EntityType } from "../../supportEntitiesTypes"
import { getAddress, getContactDetails, getInvoiceDetails } from "../../helpers"
import { SupportEntityQuery } from "../../supportEntityApi"

type EntityWithHistory = Readonly<CompanyFormValues & { history: EntityHistory }>

function transformData(data: SupportCompanyWithHistory): EntityWithHistory {
    const {
        isActive,
        businessNumber,
        isOfficeHotel,
        name,
        businessType,
        contactDetails,
        address,
        mainEanNumber,
        isSystemOwner,
        history,
        signupDetails,
        invoiceDetails,
    } = data.entity

    return {
        name,
        businessNumber,
        businessType,
        isOfficeHotel,
        isActive,
        history,
        isSystemOwner: AgreementOptions[isSystemOwner ? "YES" : "NO"],
        email: contactDetails?.email,
        contactPerson: contactDetails?.name,
        telephone: contactDetails?.phone,
        city: address?.city,
        urlIdentifier: signupDetails?.selfSignup?.urlIdentifier,
        mainEanNumber: mainEanNumber,
        postalCode: address?.postalCode,
        countryCode: address.countryCode,
        address1: address.addressLines[0],
        address2: address.addressLines[1],
        attention: invoiceDetails?.attention,
        debitorNo: invoiceDetails?.debitorNo,
        eanNumber: invoiceDetails?.endpoint.eanNumber,
        invoiceEmail: invoiceDetails?.endpoint.email,
        invoiceType: invoiceDetails?.endpoint.type,
        poNumber: invoiceDetails?.poNumber,
        reminderEmail: invoiceDetails?.reminderEmail,
        isSelfSignUp: Boolean(signupDetails?.selfSignup?.urlIdentifier),
        url: signupDetails?.selfSignup?.url,
    }
}

export function UpdateCompany() {
    const companyFormRef = useRef<FormikProps<CompanyFormValues>>(null)

    const handleSaveClick = () => {
        companyFormRef.current?.submitForm()
    }

    return (
        <>
            <LocalizedStrict id="support-update-company-title" attrs={{ title: true }}>
                <PageHeader
                    page={Page.SUPPORT_UPDATE_COMPANY}
                    title="Update company"
                    icon={<FontAwesomeIcon icon={faShop} />}
                    rightElement={
                        <LocalizedStrict id="support-create-company-update-button">
                            <Button variant="contained" onClick={handleSaveClick}>
                                Update
                            </Button>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="support-create-company-nav-item-home">
                            <BreadcrumbLink to={SUPPORT_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-company-nav-item-entities">
                            <BreadcrumbLink to={SUPPORT_ENTITIES_ROUTE}>Entities</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="support-create-company-nav-item-update-company">
                            <Breadcrumb>Update company</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>
            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <Company ref={companyFormRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const Company = forwardRef<FormikProps<CompanyFormValues>>((_props, ref) => {
    const navigate = useNavigate()
    const { entityId } = useParams()
    const entityIdNumber = Number(entityId)

    const entity = useApiQuery(fetchEntitiesById, {
        queryName: SupportEntityQuery.DETAILS,
        dependencies: {
            entityId: entityIdNumber ?? 0,
        },
        select: transformData,
        suspense: true,
        useErrorBoundary: true,
        enabled: Boolean(entityId),
    })

    const { mutate } = useApiMutation(updateCompanyApi, {
        async onSuccess(_, data, context) {
            navigate(SUPPORT_ENTITIES_ROUTE)
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.LIST] })
            await context.queryClient.invalidateQueries({ queryKey: [SupportEntityQuery.DETAILS] })
            if (data?.serviceResponse?.displayMessage) {
                enqueueSnackbar({
                    variant: "success",
                    message: data?.serviceResponse?.displayMessage,
                })
            }
        },
    })

    const editSupplier = (value: CompanyFormValues) => {
        if (!entityId) throw new Error("Entity ID is undefined")

        mutate({
            entityId,
            entity: {
                type: EntityType.COMPANY,
                name: value.name,
                isSystemOwner: value.isSystemOwner === AgreementOptions.YES,
                businessNumber: value.businessNumber,
                mainEanNumber: value.mainEanNumber,
                businessType: value.businessType as BusinessType,
                isOfficeHotel: value.isOfficeHotel,
                address: getAddress(value),
                contactDetails: getContactDetails(value),
                ...getInvoiceDetails(value),
                ...(value.isSelfSignUp && {
                    signupDetails: {
                        selfSignup: {
                            urlIdentifier: value.urlIdentifier,
                        },
                    },
                }),
                isActive: value.isActive,
            },
        })
    }

    return (
        <CompanyCreateUpdateForm
            history={entity.data?.history}
            initialValues={entity.data}
            ref={ref}
            onSubmit={editSupplier}
        />
    )
})
